import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MpSelectDropDownComponent } from './mp-select-dropdown/mp-select-dropdown.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MpCheckboxComponent } from '../mp-checkbox/mp-checkbox.component';
import { Observable } from 'rxjs';
import { DomEventListenerService } from '../../services/dom-event-listener.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { MpTooltipDirective } from '../mp-tooltip/mp-tooltip.directive';
import { DomUtils } from '../../utils/dom.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-mp-select',
  imports: [
    CommonModule,
    TranslateModule,
    MpCheckboxComponent,
    FormsModule,
    InfiniteScrollDirective,
    MpTooltipDirective,
    MpSelectDropDownComponent
  ],
  templateUrl: './mp-select.component.html',
  styleUrls: ['./mp-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpSelectComponent implements OnInit, OnChanges, OnDestroy {

  protected readonly Array = Array;

  @Input() public options: unknown[];
  /** 'small' | 'medium' | 'large' */
  @Input() public size: 'small' | 'medium' | 'large' = 'large';
  @Input() public outlined: boolean = false;
  @Input() public outlinedColor: 'primary' | 'complementary' | 'accent' | 'yellow' = 'primary';
  @Input() public outlinedStyle: 'solid' | 'dashed' = 'solid';
  @Input() public customClasses: string = '';
  @Input() public label: string;
  @Input() public faIcon: string;
  @Input() public disabled: boolean;
  @Input() public isReadOnly: boolean = false;
  @Input() public inputValue: unknown | unknown[];
  @Input() public displaySelectedValue: boolean = true;
  @Input() public formControlField: FormControl;
  @Input() public overflowTooltipAllowed: boolean = false;
  @Input() public multiple: boolean = false;
  @Input() public allowReset: boolean = true;
  @Input() public optionLabelBehavior: (option: unknown) => string;
  @Input() public optionLabelTemplate: TemplateRef<any> = null;
  @Input() public overflowTooltipBehavior: (option: unknown) => string;
  @Input() public autocompleteBehavior: (value: string) => void;
  @Input() public autocompleteSearchLabel: string = 'GLOBAL.SEARCH';
  @Input() public isSameOption: (option1: unknown, option2: unknown) => boolean;
  @Input() public fetchDataBehaviorAsync: (page: number, pageSize: number, autocompleteValue: string) => Observable<{
    content: unknown[],
    totalPages: number
  }>;
  @Input() public loadMoreElementsBehaviorAsync: () => Observable<unknown[]>;

  @Output() inputValueChange = new EventEmitter<unknown | unknown[]>();

  @ViewChild('selectField') public selectFieldElementRef: ElementRef;
  @ViewChild('autoCompleteField') public autoCompleteField: ElementRef;
  @ViewChild(MpSelectDropDownComponent, { static: false }) private readonly dropdownComponent: MpSelectDropDownComponent;
  @ViewChild(MpSelectDropDownComponent, {
    static: false,
    read: ElementRef
  }) private readonly dropdownComponentElementRef: ElementRef;
  /**
   * map de la forme {x1: y1, x2: y2} ou xn correspond à l'index de l'élément dans la liste des options (this.options)
   * et yn correspond à l'index de l'option dans la liste des options séléctionées (this.selectValue)
   */
  private multipleSelection: unknown[] = [];
  private totalPages: number;

  private autocompleteTimer: ReturnType<typeof setTimeout>;
  private _destroyRef = inject(DestroyRef);

  protected availableOptions: unknown[];
  protected selectValue: unknown | unknown[];
  protected autocompleteValue: string;
  protected fetchingData: boolean = false;
  protected highlightedOptionIndex: number = undefined;

  protected displayDropdown = signal(false);

  constructor(
      private readonly elementRef: ElementRef,
      private changeDetectorRef: ChangeDetectorRef,
      private readonly ngZone: NgZone,
      private readonly domListener: DomEventListenerService,
      private readonly translateService: TranslateService) {
  }

  ngOnInit() {
    this.subscribeToDomEvents();

    let initialSelection: unknown | unknown[];
    if (this.formControlField) {
      initialSelection = this.formControlField.value;
    } else if (this.inputValue) {
      initialSelection = this.inputValue;
    }
    if (initialSelection) {
      if (this.multiple) {
        (initialSelection as unknown[]).forEach((value) => {
          this.setSelectionState(value);
        });
      } else {
        this.setSelectionState(initialSelection);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      const options: unknown[] = this.options ? [...this.options] : [];
      this.multipleSelection.forEach((selection: unknown) => {
        const foundIndex = options.findIndex((option) => {
          return this.sameOptionBehaviorDispatcher(option, selection);
        });
        if (foundIndex === -1) {
          options.splice(0, 0, selection);
        }
      });
      this.availableOptions = options;
    }
    if (changes['formControlField']) {
      this.selectValue = this.formControlField.value;
    }
    if (changes['inputValue']) {
      this.selectValue = this.inputValue;
    }
  }

  ngOnDestroy() {
    this.removeDropdown();
  }

  public isSelectedOption(option: unknown) {
    if (this.selectValue) {
      if (this.multiple) {
        const foundIndex = this.multipleSelection.findIndex((selection) => {
          return this.sameOptionBehaviorDispatcher(option, selection);
        });
        return foundIndex > -1;
      } else {
        return this.sameOptionBehaviorDispatcher(option, this.selectValue);
      }
    } else {
      return false;
    }
  }

  public openSelectDropdown() {
    if (this.disabled || this.dropdownComponent) {
      return;
    }
    this.highlightedOptionIndex = 0;
    this.resetSearch();
    this.displayDropdown.set(true);
  }

  private subscribeToDomEvents(): void {
    this.ngZone.runOutsideAngular(() => {
      this.domListener.onScrollObservable.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({ next: (wEvent: WheelEvent) => this.onDocumentScroll(wEvent) });
      this.domListener.onClickObservable.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({ next: (mEvent: MouseEvent) => this.onDocumentClick(mEvent) });
    });
  }

  public resetSelectValue(event: Event) {
    event.stopPropagation();
    this.onValueChanged(undefined);
  }

  public canResetSelect() {
    if (this.multiple) {
      return this.allowReset && ((this.inputValue && (this.inputValue as unknown[]).length) ||
          (this.formControlField?.value && this.formControlField?.value.length));
    } else {
      return this.allowReset && (this.inputValue || this.formControlField?.value);
    }
  }

  public onValueChanged(value: unknown) {
    if (this.disabled) {
      return;
    }

    this.setSelectionState(value);

    if (!this.formControlField) {
      this.inputValue = this.selectValue;
      this.inputValueChange.emit(this.inputValue);
    } else {
      this.formControlField.setValue(this.selectValue);
      this.inputValueChange.emit(this.formControlField.value);
    }

    if (!this.multiple) {
      this.removeDropdown();
      this.focus();
    }
  }

  public hasValue() {
    return (this.inputValue && (!Array.isArray(this.inputValue) || this.inputValue.length)) ||
        this.inputValue === false || this.inputValue === 0 ||
        (this.formControlField &&
            ((this.formControlField.value && (!Array.isArray(this.formControlField.value) ||
                    this.formControlField.value.length)) ||
                this.formControlField.value === 0 || this.formControlField.value === false));
  }

  public getOptionLabel(option: unknown | unknown[]) {
    if (option && this.multiple && Array.isArray(option)) {
      let label: string = '';
      option?.forEach((item: unknown) => {
        let optionLabel: string;
        if (this.optionLabelBehavior) {
          optionLabel = this.optionLabelBehavior(item);
        } else if (option['name']) {
          optionLabel = option['name'].toString();
        } else {
          optionLabel = option.toString();
        }
        optionLabel = this.translateService.instant(optionLabel);
        label = `${label}${optionLabel}, `;
      });
      label = `${label.slice(0, -2)}`;
      return label;
    } else if (option || option === false) {
      if (this.optionLabelBehavior) {
        return this.optionLabelBehavior(option);
      } else if (option['name']) {
        return option['name'].toString();
      } else {
        return option.toString();
      }
    }
    return '';
  }

  public getOverflowTooltip(option: unknown) {

    if (!this.overflowTooltipAllowed) {
      return '';
    }

    return this.overflowTooltipBehavior ? this.overflowTooltipBehavior(option) : option?.toString();
  }

  public isChecked(option: unknown) {
    return this.multipleSelection.findIndex((selection: unknown) => {
      return this.sameOptionBehaviorDispatcher(option, selection);
    }) > -1;
  }

  public autocompleteValueChanged(value: string) {
    if (this.autocompleteTimer) {
      clearTimeout(this.autocompleteTimer);
    }

    this.autocompleteTimer = setTimeout(() => {
      if (this.fetchDataBehaviorAsync) {
        this.fetchAsyncData();
      } else if (this.autocompleteBehavior) {
        this.autocompleteBehavior(this.autocompleteValue);
      }
    }, 200);

    this.highlightedOptionIndex = 0;
  }

  public fetchAsyncData(reset: boolean = true) {
    if (this.fetchDataBehaviorAsync) {
      let page = 0;
      if (!reset) {
        page = Math.ceil(this.availableOptions.length / 10);
      }
      if (!this.fetchingData && (!this.totalPages || page <= this.totalPages)) {
        this.fetchingData = true;
        this.changeDetectorRef.detectChanges();
        this.fetchDataBehaviorAsync(page, 10, this.autocompleteValue).subscribe((response: {
          content: unknown[],
          totalPages: number
        }) => {
          this.fetchingData = false;
          this.totalPages = response.totalPages;
          this.availableOptions = reset ? response.content : [...this.availableOptions, ...response.content];
          this.changeDetectorRef.detectChanges();
        });
      }
    }
  }

  public clear() {
    this.multipleSelection = [];
    this.selectValue = undefined;
    this.inputValue = undefined;
  }

  private resetSearch() {
    if (this.autocompleteValue) {
      this.autocompleteValue = undefined;
      this.availableOptions = this.options ? [...this.options] : [];
    }
  }

  private sameOptionBehaviorDispatcher(option1: unknown, option2: unknown) {
    if (this.isSameOption) {
      return this.isSameOption(option1, option2);
    } else if (option1['id'] && option2['id']) {
      return option1['id'] === option2['id'];
    } else {
      return option2 === option1;
    }
  }

  isEllipsisActive() {
    const valueElements: NodeList = this.selectFieldElementRef?.nativeElement?.querySelectorAll('.mp-select-value span');

    if (!valueElements) {return true;}
    return Array.from(valueElements).some((element: HTMLElement) => element && DomUtils.isEllipsisActive(element));
  };

  private setSelectionState(value: unknown) {
    if (this.multiple) {
      if (value) {
        const foundIndex = this.multipleSelection.findIndex((selection) => {
          return this.sameOptionBehaviorDispatcher(value, selection);
        });
        if (foundIndex > -1) {
          this.multipleSelection.splice(foundIndex, 1);
        } else {
          this.multipleSelection.push(value);
        }
      } else {
        this.multipleSelection = [];
      }
      this.selectValue = [...this.multipleSelection];
    } else {
      this.selectValue = value;
    }

    this.changeDetectorRef.detectChanges();
  }

  private removeDropdown(): void {
    this.displayDropdown.set(false);
    this.highlightedOptionIndex = undefined;
  }

  private onDocumentScroll(event): void {
    if (this.dropdownComponentElementRef && !this.dropdownComponentElementRef?.nativeElement.contains(event.target as Node)) {
      this.ngZone.run(() => this.removeDropdown());
    }
  }

  private onDocumentClick(event): void {
    if (!this.elementRef?.nativeElement.contains(event.target)) {
      this.ngZone.run(() => {
        this.removeDropdown();
      });
    }
  }

  protected onDocumentKeydown(event): void {
    if (!this.dropdownComponent) {
      return;
    }

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (this.highlightedOptionIndex || this.highlightedOptionIndex > 0) {
        this.highlightedOptionIndex = this.highlightedOptionIndex - 1;
      } else {
        this.highlightedOptionIndex = 0;
      }
      this.scrollList();
    }

    if (event.code === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (this.highlightedOptionIndex || this.highlightedOptionIndex === 0) {
        if (this.highlightedOptionIndex < this.availableOptions.length - 1) {
          this.highlightedOptionIndex = this.highlightedOptionIndex + 1;
        } else {
          this.highlightedOptionIndex = this.availableOptions.length - 1;
        }
      } else {
        this.highlightedOptionIndex = 0;
      }
      this.scrollList();
    }

    if (event.code === 'NumpadEnter' || event.code === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const index = this.highlightedOptionIndex % this.availableOptions.length;
      this.onValueChanged(this.availableOptions[index]);
    }

    if (event.shiftKey && event.code === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      this.removeDropdown();
      this.focus();
    }

    if (!event.shiftKey && event.code === 'Tab' && this.dropdownComponent) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private scrollList() {
    this.ngZone.runOutsideAngular(() => {
      const top: number = this.dropdownComponentElementRef.nativeElement.children[0].children[1].scrollTop;
      // 4 * 48 is because each option is 48px tall and the dropdown maximum size allow for 4 options displayed at a time
      const bottom: number = this.dropdownComponentElementRef.nativeElement.children[0].children[1].scrollTop + (4 * 48);
      const minTop: number = this.highlightedOptionIndex * 48;
      const maxTop: number = (this.highlightedOptionIndex + 1) * 48;
      if (top > minTop) {
        const newTop = top - (top - minTop);
        this.dropdownComponentElementRef.nativeElement.children[0].children[1].scrollTo({ top: newTop });
      } else if (bottom < maxTop) {
        const newTop = top + (maxTop - bottom);
        this.dropdownComponentElementRef.nativeElement.children[0].children[1].scrollTo({ top: newTop });
      }
    });
  }

  private focus() {
    this.elementRef?.nativeElement?.children[0]?.focus();
  }
}
